/**
 * Add/Edit Catalog Item Screen
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Image,
  Alert,
} from 'react-native';
import { Catalog, CatalogCreateRequest, catalogService, StorageService, type UploadProgress } from '@app/core';
import { useShopId } from '@app/core';
import { Button, Input, Card, Text } from '@app/design-system';
import { colors } from '@app/design-system';

const storageService = new StorageService();

interface AddEditCatalogScreenProps {
  item?: Catalog;
  onSaveSuccess?: (item: Catalog) => void;
  onCancel?: () => void;
}

interface CatalogForm {
  name: string;
  category: string;
  description: string;
  unit: string;
  price: string;
  currency: string;
  blobPath: string;
  status: string;
  isAvailable: boolean;
  stockAvailable: string;
  stockReserved: string;
  stockTotal: string;
}

const UNITS = ['kg', 'piece', 'liter', 'gram', 'dozen'];
const STATUSES = ['ACTIVE', 'INACTIVE', 'OUT_OF_STOCK'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'];
const CATEGORIES = [
  'Food & Beverages',
  'Electronics',
  'Clothing & Fashion',
  'Home & Garden',
  'Health & Beauty',
  'Sports & Fitness',
  'Books & Media',
  'Toys & Games',
  'Automotive',
  'Other'
];

export const AddEditCatalogScreen: React.FC<AddEditCatalogScreenProps> = ({
  item,
  onSaveSuccess,
  onCancel,
}) => {
  const [form, setForm] = useState<CatalogForm>({
    name: '',
    category: '',
    description: '',
    unit: 'kg', // Changed default to kg
    price: '1', // Default to 1 to ensure price > 0
    currency: 'INR',
    blobPath: '', // Changed from imageUrl
    status: 'ACTIVE',
    isAvailable: true,
    stockAvailable: '0',
    stockReserved: '0',
    stockTotal: '0',
  });

  const [errors, setErrors] = useState<Partial<CatalogForm>>({});
  const [loading, setLoading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>(''); // Preview URL from backend
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shopId = useShopId();

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name,
        category: item.category,
        description: item.description || '',
        unit: item.unit,
        price: item.price.value.toString(),
        currency: 'INR', // Always default to INR for editing
        blobPath: item.imageUrl || '', // imageUrl from backend is actually blobPath
        status: item.status,
        isAvailable: item.availability?.available ?? true,
        stockAvailable: item.stock?.available?.toString() || '0',
        stockReserved: item.stock?.reserved?.toString() || '0',
        stockTotal: item.stock?.total?.toString() || '0',
      });

      // Load preview URL if blobPath exists
      if (item.imageUrl) {
        loadImagePreview(item.imageUrl);
      }
    }
  }, [item]);

  // Load image preview URL from backend
  const loadImagePreview = async (blobPath: string) => {
    if (!blobPath) return;

    try {
      console.log('🔍 [AddEdit] Loading preview for blobPath:', blobPath);
      const readUrl = await storageService.getReadUrl(blobPath);
      setImagePreviewUrl(readUrl);
      setImagePreviewError(false);
      console.log('✅ [AddEdit] Preview URL loaded successfully');
    } catch (error) {
      console.error('❌ [AddEdit] Failed to load preview URL:', error);
      setImagePreviewError(true);
    }
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('📁 [AddEdit] File selected:', file.name);

    // Validate file
    const validation = storageService.validateFile(file);
    if (!validation.valid) {
      Alert.alert('Invalid File', validation.error || 'Please select a valid image file');
      return;
    }

    setSelectedFile(file);

    // Show local preview immediately
    const localPreviewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(localPreviewUrl);
    setImagePreviewError(false);

    console.log('✅ [AddEdit] File validated and preview set');
  };

  // Handle file upload to Azure
  const handleImageUpload = async (): Promise<string | null> => {
    if (!selectedFile || !shopId) {
      return null;
    }

    console.log('☁️ [AddEdit] Starting secure upload...');
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const blobPath = await storageService.uploadImage(
        selectedFile,
        shopId,
        'catalog',
        item?.id, // Use existing item ID if editing
        (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
          console.log(`📊 [AddEdit] Upload progress: ${progress.percentage}%`);
        }
      );

      console.log('✅ [AddEdit] Upload completed, blobPath:', blobPath);

      // Load preview URL for the uploaded blob
      await loadImagePreview(blobPath);

      setIsUploading(false);
      setUploadProgress(100);

      return blobPath;
    } catch (error) {
      console.error('❌ [AddEdit] Upload failed:', error);
      setIsUploading(false);
      setUploadProgress(0);

      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      Alert.alert('Upload Failed', errorMessage);

      return null;
    }
  };

  // Trigger file input click
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setShowCategoryDropdown(false);
    setShowUnitDropdown(false);
    setShowCurrencyDropdown(false);
  };

  const updateForm = (field: keyof CatalogForm, value: string): void => {
    setForm((prev) => ({
      ...prev,
      [field]: field === 'isAvailable' ? value === 'true' : value
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Close dropdowns when updating form
    closeAllDropdowns();
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CatalogForm> = {};

    if (!form.name.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (!form.category.trim()) {
      newErrors.category = 'Category is required';
    }

    if (!form.price.trim()) {
      newErrors.price = 'Price is required';
    } else if (isNaN(parseFloat(form.price)) || parseFloat(form.price) <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSave = async (): Promise<void> => {
    if (!validateForm() || !shopId) {
      return;
    }

    setLoading(true);

    try {
      // Upload image if a new file was selected
      let finalBlobPath = form.blobPath;
      if (selectedFile) {
        const uploadedBlobPath = await handleImageUpload();
        if (uploadedBlobPath) {
          finalBlobPath = uploadedBlobPath;
        }
      }

      const priceValue = parseFloat(form.price);
      if (priceValue <= 0) {
        Alert.alert('Error', 'Price must be greater than 0');
        return;
      }

      const stockAvailable = parseInt(form.stockAvailable) || 0;
      const stockReserved = parseInt(form.stockReserved) || 0;
      const stockTotal = parseInt(form.stockTotal) || stockAvailable + stockReserved;

      const request: CatalogCreateRequest = {
        name: form.name,
        category: form.category,
        shopId,
        unit: form.unit as any,
        price: {
          currency: form.currency,
          value: priceValue,
        },
        description: form.description || undefined,
        imageUrl: finalBlobPath || undefined, // Send blobPath as imageUrl
        availability: {
          available: form.isAvailable,
        },
        stock: {
          available: stockAvailable,
          reserved: stockReserved,
          total: stockTotal,
        },
      };

      console.log('💾 [AddEdit] Saving catalog with blobPath:', finalBlobPath);

      const savedItem = item
        ? await catalogService.updateCatalogItem({ ...request, id: item.id })
        : await catalogService.createCatalogItem(request);

      Alert.alert('Success', `Product ${item ? 'updated' : 'created'} successfully!`);

      if (onSaveSuccess) {
        onSaveSuccess(savedItem);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save product';
      Alert.alert('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={closeAllDropdowns}
      activeOpacity={1}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text variant="h2" bold>
            {item ? 'Edit Product' : 'Add Product'}
          </Text>
        </View>

        <Card style={styles.formCard}>
        <Input
          label="Product Name"
          placeholder="e.g., Margherita Pizza"
          value={form.name}
          onChangeText={(value) => updateForm('name', value)}
          error={errors.name}
          testID="catalog-name-input"
        />

        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Category *</Text>
          <TouchableOpacity
            style={[styles.dropdown, errors.category && styles.dropdownError]}
            onPress={() => setShowCategoryDropdown(!showCategoryDropdown)}
            testID="catalog-category-dropdown"
          >
            <Text style={[styles.dropdownText, !form.category && styles.dropdownPlaceholder]}>
              {form.category || 'Select category'}
            </Text>
            <Text style={styles.dropdownArrow}>{showCategoryDropdown ? '▲' : '▼'}</Text>
          </TouchableOpacity>
          {showCategoryDropdown && (
            <View style={styles.dropdownOptions}>
              {CATEGORIES.map((category) => (
                <TouchableOpacity
                  key={category}
                  style={styles.dropdownOption}
                  onPress={() => {
                    updateForm('category', category);
                    setShowCategoryDropdown(false);
                  }}
                  testID={`category-option-${category}`}
                >
                  <Text style={styles.dropdownOptionText}>{category}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
          {errors.category && (
            <Text style={styles.errorText}>{errors.category}</Text>
          )}
        </View>

        <Input
          label="Description"
          placeholder="Optional description"
          value={form.description}
          onChangeText={(value) => updateForm('description', value)}
          testID="catalog-description-input"
        />

        <View style={styles.row}>
          <View style={styles.halfInput}>
            <Text style={styles.label}>Unit *</Text>
            <TouchableOpacity
              style={styles.dropdown}
              onPress={() => setShowUnitDropdown(!showUnitDropdown)}
              testID="catalog-unit-select"
            >
              <Text style={styles.dropdownText}>{form.unit}</Text>
              <Text style={styles.dropdownArrow}>{showUnitDropdown ? '▲' : '▼'}</Text>
            </TouchableOpacity>
            {showUnitDropdown && (
              <View style={styles.dropdownOptions}>
                {UNITS.map((unit) => (
                  <TouchableOpacity
                    key={unit}
                    style={styles.dropdownOption}
                    onPress={() => {
                      updateForm('unit', unit);
                      setShowUnitDropdown(false);
                    }}
                    testID={`unit-option-${unit}`}
                  >
                    <Text style={styles.dropdownOptionText}>{unit}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={styles.halfInput}>
            <View style={styles.priceContainer}>
              <View style={styles.priceInput}>
                <Input
                  label="Price"
                  placeholder="0.00"
                  value={form.price}
                  onChangeText={(value) => updateForm('price', value)}
                  keyboardType="decimal-pad"
                  error={errors.price}
                  testID="catalog-price-input"
                />
              </View>
              <View style={styles.currencyContainer}>
                <Text style={styles.label}>Currency</Text>
                <TouchableOpacity
                  style={styles.currencyDropdown}
                  onPress={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                  testID="catalog-currency-select"
                >
                  <Text style={styles.dropdownText}>{form.currency}</Text>
                  <Text style={styles.dropdownArrow}>{showCurrencyDropdown ? '▲' : '▼'}</Text>
                </TouchableOpacity>
                {showCurrencyDropdown && (
                  <View style={styles.currencyDropdownOptions}>
                    {CURRENCIES.map((currency) => (
                      <TouchableOpacity
                        key={currency}
                        style={styles.dropdownOption}
                        onPress={() => {
                          updateForm('currency', currency);
                          setShowCurrencyDropdown(false);
                        }}
                        testID={`currency-option-${currency}`}
                      >
                        <Text style={styles.dropdownOptionText}>{currency}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>

        <View style={styles.imageUploadSection}>
          <Text style={styles.label}>Product Image</Text>
          <Text style={styles.helperText}>
            Upload a brand image for your product (max 5MB)
          </Text>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Browse button */}
          <TouchableOpacity
            style={styles.browseButton}
            onPress={handleBrowseClick}
            disabled={isUploading}
            testID="catalog-browse-image"
          >
            <Text style={styles.browseButtonText}>
              {selectedFile ? '📁 Change Image' : '📁 Browse Image'}
            </Text>
          </TouchableOpacity>

          {/* Upload progress */}
          {isUploading && (
            <View style={styles.uploadProgress}>
              <View style={styles.progressBarContainer}>
                <View style={[styles.progressBar, { width: `${uploadProgress}%` }]} />
              </View>
              <Text style={styles.progressText}>{uploadProgress}%</Text>
            </View>
          )}

          {/* Selected file info */}
          {selectedFile && !isUploading && (
            <View style={styles.fileInfo}>
              <Text style={styles.fileName}>
                ✓ {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </Text>
            </View>
          )}

          {/* Image preview */}
          {imagePreviewUrl && !imagePreviewError && (
            <View style={styles.imagePreview}>
              <Text style={styles.previewLabel}>Preview:</Text>
              <div style={{
                width: 200,
                height: 200,
                borderRadius: 8,
                border: '1px solid #ddd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
                margin: '0 auto'
              }}>
                <img
                  src={imagePreviewUrl}
                  alt="Catalog preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'cover',
                    borderRadius: 8
                  }}
                  onLoad={() => console.log('✅ [AddEdit] Image preview loaded')}
                  onError={() => {
                    console.error('❌ [AddEdit] Image preview error');
                    setImagePreviewError(true);
                  }}
                />
              </div>
            </View>
          )}

          {imagePreviewError && form.blobPath && (
            <View style={styles.imageFallback}>
              <Text variant="caption" color="error">
                Image could not be loaded
              </Text>
            </View>
          )}
        </View>

        {/* Availability Section */}
        <View style={styles.availabilitySection}>
          <Text style={styles.label}>Availability</Text>
          <TouchableOpacity
            style={styles.checkboxContainer}
            onPress={() => updateForm('isAvailable', (!form.isAvailable).toString())}
            testID="catalog-availability-checkbox"
          >
            <View style={[styles.checkbox, form.isAvailable && styles.checkboxChecked]}>
              {form.isAvailable && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <Text style={styles.checkboxLabel}>Available for purchase</Text>
          </TouchableOpacity>
        </View>

        {/* Stock Section */}
        <View style={styles.stockSection}>
          <Text style={styles.label}>Stock Management</Text>
          <View style={styles.stockRow}>
            <View style={styles.stockInput}>
              <Input
                label="Available"
                placeholder="0"
                value={form.stockAvailable}
                onChangeText={(value) => updateForm('stockAvailable', value)}
                keyboardType="numeric"
                testID="catalog-stock-available"
              />
            </View>
            <View style={styles.stockInput}>
              <Input
                label="Reserved"
                placeholder="0"
                value={form.stockReserved}
                onChangeText={(value) => updateForm('stockReserved', value)}
                keyboardType="numeric"
                testID="catalog-stock-reserved"
              />
            </View>
            <View style={styles.stockInput}>
              <Input
                label="Total"
                placeholder="0"
                value={form.stockTotal}
                onChangeText={(value) => updateForm('stockTotal', value)}
                keyboardType="numeric"
                testID="catalog-stock-total"
              />
            </View>
          </View>
        </View>

        <View style={styles.statusSection}>
          <Text style={styles.label}>Status</Text>
          <View style={styles.statusButtons}>
            {STATUSES.map((status) => (
              <TouchableOpacity
                key={status}
                style={[
                  styles.statusButton,
                  form.status === status && styles.statusButtonActive,
                ]}
                onPress={() => updateForm('status', status)}
                testID={`catalog-status-${status}`}
              >
                <Text
                  style={[
                    styles.statusButtonText,
                    form.status === status && styles.statusButtonTextActive,
                  ]}
                >
                  {status}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.actions}>
          <Button
            variant="secondary"
            onPress={onCancel}
            style={styles.button}
            testID="catalog-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onPress={handleSave}
            disabled={loading}
            loading={loading}
            style={styles.button}
            testID="catalog-save-button"
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </View>
        </Card>
      </ScrollView>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.gray1,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  header: {
    marginBottom: 16,
  },
  formCard: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 4,
  },
  fieldContainer: {
    marginBottom: 16,
    position: 'relative',
    zIndex: 1,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
    position: 'relative',
    zIndex: 1,
  },
  halfInput: {
    flex: 1,
    position: 'relative',
  },
  // Enhanced dropdown styles
  dropdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
    minHeight: 48,
  },
  dropdownError: {
    borderColor: colors.error,
  },
  dropdownText: {
    fontSize: 16,
    color: colors.gray9,
    flex: 1,
  },
  dropdownPlaceholder: {
    color: colors.gray6,
  },
  dropdownArrow: {
    fontSize: 12,
    color: colors.gray6,
    marginLeft: 8,
  },
  dropdownOptions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderTopWidth: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    maxHeight: 200,
    zIndex: 1000,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  dropdownOption: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  dropdownOptionText: {
    fontSize: 16,
    color: colors.gray9,
  },
  errorText: {
    fontSize: 12,
    color: colors.error,
    marginTop: 4,
  },
  // Price and currency container styles
  priceContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  priceInput: {
    flex: 2,
  },
  currencyContainer: {
    flex: 1,
    position: 'relative',
  },
  currencyDropdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
    minHeight: 48,
  },
  currencyDropdownOptions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderTopWidth: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    zIndex: 1000,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  // Legacy unit select (removed)
  unitSelect: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
  },
  // Image upload section
  imageUploadSection: {
    marginVertical: 16,
  },
  helperText: {
    fontSize: 12,
    color: colors.gray6,
    marginBottom: 12,
  },
  browseButton: {
    backgroundColor: colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  browseButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  uploadProgress: {
    marginBottom: 12,
  },
  progressBarContainer: {
    height: 8,
    backgroundColor: colors.gray2,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressBar: {
    height: '100%',
    backgroundColor: colors.primary,
  },
  progressText: {
    fontSize: 12,
    color: colors.gray7,
    textAlign: 'center',
  },
  fileInfo: {
    backgroundColor: colors.successLight || '#E8F5E9',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  fileName: {
    fontSize: 14,
    color: colors.success || '#4CAF50',
    fontWeight: '500',
  },
  previewLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 8,
  },
  imagePreview: {
    marginVertical: 12,
    alignItems: 'center',
  },
  previewImage: {
    width: 120,
    height: 120,
    borderRadius: 8,
    backgroundColor: colors.gray2,
  },
  imageFallback: {
    backgroundColor: colors.errorLight,
    padding: 12,
    borderRadius: 8,
    marginVertical: 12,
  },
  statusSection: {
    marginVertical: 12,
  },
  statusButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  statusButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
    alignItems: 'center',
  },
  statusButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  statusButtonText: {
    color: colors.gray9,
    fontWeight: '600',
    fontSize: 12,
  },
  statusButtonTextActive: {
    color: colors.white,
  },
  availabilitySection: {
    marginVertical: 12,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: colors.gray3,
    borderRadius: 4,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkmark: {
    color: colors.white,
    fontSize: 12,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 16,
    color: colors.gray9,
  },
  stockSection: {
    marginVertical: 12,
  },
  stockRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  stockInput: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  button: {
    flex: 1,
  },
});

